import {z} from 'zod';

import {includes, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  VehicleFinancingInterestRateTypeSchema,
  VehicleFinancingPaymentStepSchema,
  VehicleFinancingScopeModeSchema,
  VehicleFinancingTemplateTypeSchema,
  VehicleFinancingTemplateExpirationActionSchema,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {RateMatrixValueSchema} from '../../RateMatrix/types/RateMatrixValue';
import {isValidBalloonCombination} from '../../RateMatrix/utils/isValidBalloonCombination';
import {parseRateMatrixCellKey} from '../../RateMatrix/utils/parseRateMatrixCellKey';

const NAME_MAX_LENGTH = 255;

const ValidityModeFormSchema = z.enum(['UNLIMITED', 'PERIOD']);

const NonNegativeAmountSchema = z
  .number()
  .min(0, {message: i18n.t('general.errors.number.greaterOrEqual', {min: 0})})
  .nullable();

const VehicleSegmentFormSchema = z.object({
  makes: z.array(z.string()),
  models: z.array(z.string()),
  vehicleCategories: z.array(z.string()),
  fuelTypes: z.array(z.string()),
  yearFrom: z.number().nullable(),
  yearTo: z.number().nullable(),
  mileageFrom: z.number().nullable(),
  mileageTo: z.number().nullable(),
  sellingPriceFrom: NonNegativeAmountSchema,
  sellingPriceTo: NonNegativeAmountSchema,
});

const ScopeFormSchema = z.object({
  mode: VehicleFinancingScopeModeSchema,
  branchIds: z.array(z.string()),
  segment: VehicleSegmentFormSchema,
});

export const vehicleFinancingTemplateFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, {message: i18n.t('general.errors.mixed.required')})
      .max(NAME_MAX_LENGTH),
    type: z
      .array(VehicleFinancingTemplateTypeSchema)
      .min(1, {message: i18n.t('general.errors.mixed.required')}),
    // Form-only: which validity editor is open. It lives in the form (not in component state) so
    // `reset()` restores it together with the date fields it governs — otherwise a discard leaves
    // the segment showing "Unlimited" while the restored payload still carries the dates.
    // `mapFormValuesToRequestBody` builds the request body field by field, so it never reaches
    // the wire contract (which has no such property).
    validityMode: ValidityModeFormSchema,
    validFrom: z.string().nullable(),
    validTo: z.string().nullable(),
    expirationAction: VehicleFinancingTemplateExpirationActionSchema.nullable(),
    replacementTemplateId: z.string().nullable(),
    interestRateType: z.array(VehicleFinancingInterestRateTypeSchema),
    downPaymentStep: z
      .array(VehicleFinancingPaymentStepSchema)
      .min(1, {message: i18n.t('general.errors.mixed.required')}),
    balloonPaymentStep: z.array(VehicleFinancingPaymentStepSchema),
    financialInstitutionCodeId: z
      .string()
      .min(1, {message: i18n.t('general.errors.mixed.required')}),
    processingFee: NonNegativeAmountSchema,
    vehicleTransferIncluded: z.boolean(),
    vehicleTransferAmount: NonNegativeAmountSchema,
    vehicleRegistrationIncluded: z.boolean(),
    vehicleRegistrationAmount: NonNegativeAmountSchema,
    vehicleDeliveryIncluded: z.boolean(),
    vehicleDeliveryAmount: NonNegativeAmountSchema,
    tradeInPossible: z.boolean(),
    creditProtectionAutoEnable: z.boolean(),
    scope: ScopeFormSchema,
    // Rate matrix editor state (Task 4). Controlled by `RateMatrix`; serialized to the flat
    // `cells[]` + `defaultBalloonPayment` wire fields on submit (serializeRateMatrix).
    rateMatrix: RateMatrixValueSchema,
  })
  .superRefine((values, ctx) => {
    if (values.scope.mode === 'BRANCH' && values.scope.branchIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.required'),
        path: ['scope', 'branchIds'],
      });
    }

    if (values.type[0] === 'BALLOON' && values.balloonPaymentStep.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.required'),
        path: ['balloonPaymentStep'],
      });
    }

    if (values.type[0] === 'BALLOON' && values.rateMatrix.defaultBalloonPayment === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.required'),
        path: ['rateMatrix'],
      });
    }

    const serializableBalloonCoordinates = Object.keys(values.rateMatrix.cells)
      .map(parseRateMatrixCellKey)
      .filter(
        ({term, balloonBracket}) =>
          isNotNil(balloonBracket) && includes(term, values.rateMatrix.availableTerms)
      );
    const hasCellInDefaultBalloonBracket = serializableBalloonCoordinates.some(
      ({balloonBracket}) => balloonBracket === values.rateMatrix.defaultBalloonPayment
    );

    if (
      values.type[0] === 'BALLOON' &&
      isNotNil(values.rateMatrix.defaultBalloonPayment) &&
      serializableBalloonCoordinates.length > 0 &&
      !hasCellInDefaultBalloonBracket
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.invalid'),
        path: ['rateMatrix'],
      });
    }

    // The vehicle financing contract rejects any cell where the down payment and the balloon
    // payment leave nothing to finance (`FinancingRateMatrixSchema`). Enforcing it only there
    // let the editor author cells that the vehicle matrix then renders as disabled — blocking
    // every save of that vehicle with no way to reach the offending cell and remove it.
    const hasInvalidBalloonCoordinate = Object.keys(values.rateMatrix.cells).some((cellKey) => {
      const {downPayment, balloonBracket} = parseRateMatrixCellKey(cellKey);

      return !isValidBalloonCombination(downPayment, balloonBracket);
    });

    if (hasInvalidBalloonCoordinate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.invalid'),
        path: ['rateMatrix'],
      });
    }

    const addInvalidRangeIssue = (path: string[]) =>
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.invalidRange'),
        path,
      });

    // `apiDate` fields hold ISO `YYYY-MM-DD` strings, so lexicographic comparison is safe.
    if (
      isNotNil(values.validFrom) &&
      isNotNil(values.validTo) &&
      values.validFrom > values.validTo
    ) {
      addInvalidRangeIssue(['validTo']);
    }

    const hasEndDate = isNotNil(values.validTo);
    if (hasEndDate && values.expirationAction === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.required'),
        path: ['expirationAction'],
      });
    }
    if (
      hasEndDate &&
      values.expirationAction === 'REPLACE' &&
      isNilOrEmpty(values.replacementTemplateId)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.errors.mixed.required'),
        path: ['replacementTemplateId'],
      });
    }

    if (values.scope.mode !== 'SEGMENT') {
      return;
    }

    const segment = values.scope.segment;
    if (
      isNotNil(segment.yearFrom) &&
      isNotNil(segment.yearTo) &&
      segment.yearFrom > segment.yearTo
    ) {
      addInvalidRangeIssue(['scope', 'segment', 'yearTo']);
    }
    if (
      isNotNil(segment.mileageFrom) &&
      isNotNil(segment.mileageTo) &&
      segment.mileageFrom > segment.mileageTo
    ) {
      addInvalidRangeIssue(['scope', 'segment', 'mileageTo']);
    }
    if (
      isNotNil(segment.sellingPriceFrom) &&
      isNotNil(segment.sellingPriceTo) &&
      segment.sellingPriceFrom > segment.sellingPriceTo
    ) {
      addInvalidRangeIssue(['scope', 'segment', 'sellingPriceTo']);
    }
  });

export type VehicleFinancingTemplateFormValues = z.infer<typeof vehicleFinancingTemplateFormSchema>;
